import { Button } from '@fountain/ripple/button';
import { ButtonIcon } from '@fountain/ripple/button-icon';
import { Counter } from '@fountain/ripple/counter';
import { DropdownMenu } from '@fountain/ripple/dropdown-menu';
import { Tag } from '@fountain/ripple/tag';
import { Text } from '@fountain/ripple/text';
import { TextField } from '@fountain/ripple/text-field';
import {
  ArrowDownOutlineIcon,
  ArrowUpOutlineIcon,
  DotsVerticalOutlineIcon,
  PencilAltOutlineIcon,
  PlusOutlineIcon,
  TrashOutlineIcon,
} from '@fountain/ripple/icon';
import React, { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { messages as globalMessages } from 'shared/global/messages';

import { messages } from './ReportRequests.messages';
import { useStyles } from './styles';
import type { ReportTestInput, useReportTest } from './useReportTest';

type Report = ReportTestInput['config']['reports'][number];
export interface ReportRequestsProps {
  reports: Report[];
  onChange: (reports: Report[]) => void;
  onRun: (reportKey: string) => void;
  sources: ReturnType<typeof useReportTest>['sources'];
  running: boolean;
  fieldErrors: Record<string, { name?: string; path?: string }>;
}

export function ReportRequests({
  reports,
  onChange,
  onRun,
  sources,
  running,
  fieldErrors,
}: ReportRequestsProps) {
  const intl = useIntl();
  const styles = useStyles();
  const [editing, setEditing] = useState<string | null>(null);
  const [dismissedErrors, setDismissedErrors] = useState<
    ReportRequestsProps['fieldErrors'] | null
  >(null);
  const addButton = useRef<HTMLButtonElement>(null);
  const editButtons = useRef(new Map<string, HTMLButtonElement>());
  const format = intl.formatMessage;
  const editingKey =
    editing ??
    (fieldErrors !== dismissedErrors
      ? reports.find(
          report =>
            fieldErrors[report.key]?.name || fieldErrors[report.key]?.path,
        )?.key
      : null);

  function move(key: string, direction: number) {
    const index = reports.findIndex(report => report.key === key);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= reports.length) return;
    const next = [...reports];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }

  function add() {
    if (reports.length >= 3) return;
    const key = uuid();
    onChange([
      ...reports,
      {
        key,
        name: format(messages.newReport),
        path: '',
        tool: 'workday',
        api: 'workday-custom-report',
        population: 'mapped',
        parameters: reports[0]?.parameters ?? [
          { key: 'format', value: { literal: 'json' } },
        ],
        columns: {},
      },
    ]);
    setEditing(key);
  }

  return (
    <section aria-label={format(messages.reports)}>
      <div className={styles.reportHeading}>
        <Text variant="body2">{format(messages.reports)}</Text>
        <Text variant="subtitle1">{format(messages.limit)}</Text>
      </div>
      <Text variant="subtitle1">{format(messages.allReports)}</Text>
      <ol className={styles.reportList}>
        {reports.map((report, index) => {
          const name = report.name || format(messages.newReport);
          const source = sources.find(item => item.reportKey === report.key);
          const ready =
            source?.status === 'complete' &&
            source.rowCount !== null &&
            source.rowCount > 0;
          const failed = source?.status === 'unavailable';
          let status: {
            color: 'green' | 'rose' | 'amber';
            message: typeof messages.ready;
          } = { color: 'amber', message: messages.needed };
          if (ready) status = { color: 'green', message: messages.ready };
          if (failed) status = { color: 'rose', message: messages.errors };
          const update = (updates: Partial<Report>) =>
            onChange(
              reports.map(item =>
                item.key === report.key ? { ...item, ...updates } : item,
              ),
            );
          return (
            <li key={report.key} className={styles.reportItem}>
              <div className={styles.reportRow}>
                <Counter value={index + 1} aria-hidden="true" />
                <Text variant="body2" className={styles.reportName}>
                  {name}
                </Text>
                {editingKey === report.key ? (
                  <Button
                    size="sm"
                    variant="secondary-neutral"
                    onClick={() => {
                      flushSync(() => {
                        setEditing(null);
                        setDismissedErrors(fieldErrors);
                      });
                      editButtons.current.get(report.key)?.focus();
                    }}
                  >
                    {format(globalMessages.done)}
                  </Button>
                ) : (
                  <>
                    <Tag size="sm" color={status.color}>
                      {format(status.message)}
                    </Tag>
                    <ButtonIcon
                      ref={element => {
                        if (element)
                          editButtons.current.set(report.key, element);
                        else editButtons.current.delete(report.key);
                      }}
                      size="sm"
                      variant="text-neutral"
                      icon={PencilAltOutlineIcon}
                      aria-label={format(messages.edit, { report: name })}
                      onClick={() => setEditing(report.key)}
                    />
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger
                        render={
                          <ButtonIcon
                            size="sm"
                            variant="text-neutral"
                            icon={DotsVerticalOutlineIcon}
                            aria-label={format(messages.actions, {
                              report: name,
                            })}
                          />
                        }
                      />
                      <DropdownMenu.Portal>
                        <DropdownMenu.Positioner>
                          <DropdownMenu.Popup>
                            <DropdownMenu.Item
                              startIcon={ArrowUpOutlineIcon}
                              disabled={index === 0}
                              onClick={() => move(report.key, -1)}
                            >
                              {format(messages.up)}
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              startIcon={ArrowDownOutlineIcon}
                              disabled={index === reports.length - 1}
                              onClick={() => move(report.key, 1)}
                            >
                              {format(messages.down)}
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              startIcon={TrashOutlineIcon}
                              variant="destructive"
                              onClick={() => {
                                flushSync(() =>
                                  onChange(
                                    reports.filter(
                                      item => item.key !== report.key,
                                    ),
                                  ),
                                );
                                addButton.current?.focus();
                              }}
                            >
                              {format(globalMessages.remove)}
                            </DropdownMenu.Item>
                          </DropdownMenu.Popup>
                        </DropdownMenu.Positioner>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </>
                )}
              </div>
              {editingKey === report.key && (
                <div className={styles.reportEditor}>
                  <div className={styles.reportFields}>
                    <TextField
                      size="sm"
                      autoFocus
                      label={format(globalMessages.name)}
                      value={report.name}
                      error={fieldErrors[report.key]?.name}
                      onChange={event => update({ name: event.target.value })}
                    />
                    <TextField
                      size="sm"
                      label={format(messages.path)}
                      value={report.path}
                      error={fieldErrors[report.key]?.path}
                      onChange={event => update({ path: event.target.value })}
                    />
                  </div>
                  <div className={styles.reportRow}>
                    <Button
                      size="sm"
                      variant="secondary-brand"
                      disabled={running}
                      loading={running}
                      onClick={() => onRun(report.key)}
                    >
                      {format(messages.run)}
                    </Button>
                    <span role="status">
                      {failed && (
                        <Text variant="subtitle1">
                          {format(messages.errors)}
                        </Text>
                      )}
                      {source?.status === 'complete' && (
                        <Text variant="subtitle1">
                          {format(
                            source.rowCount === 0
                              ? messages.noRows
                              : messages.columns,
                            { count: source.columnNames.length },
                          )}
                        </Text>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {reports.length < 3 && (
        <Button
          ref={addButton}
          size="sm"
          variant="text-brand"
          startIcon={PlusOutlineIcon}
          onClick={add}
        >
          {format(messages.add)}
        </Button>
      )}
    </section>
  );
}
